import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Phone, Mail, Building2, User, MessageSquare, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSalesForm = ({ 
  trigger, 
  productInterest = null,
  buttonText = "Contact Sales"
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_interest: productInterest || "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success("Thank you! Our team will contact you shortly.");
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          product_interest: productInterest || "",
          message: ""
        });
      }, 2000);
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="contact-sales-btn">
            <Phone className="w-4 h-4 mr-2" />
            {buttonText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="contact-sales-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-800">
            Contact Sales Team
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              Message Sent!
            </h3>
            <p className="text-slate-600">
              Our sales team will contact you within 24 hours.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                Full Name *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="John Smith"
                required
                className="mt-1"
                data-testid="contact-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                Email Address *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@company.com"
                required
                className="mt-1"
                data-testid="contact-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  className="mt-1"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <Label htmlFor="company" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  Company
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Company Name"
                  className="mt-1"
                  data-testid="contact-company"
                />
              </div>
            </div>

            {productInterest && (
              <div className="bg-slate-50 rounded-lg p-3">
                <span className="text-xs font-medium text-slate-500 uppercase">
                  Product Interest
                </span>
                <p className="text-sm font-semibold text-slate-800 mt-1">
                  {productInterest}
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-slate-400" />
                Message *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your application, system size, or any questions..."
                rows={4}
                required
                className="mt-1"
                data-testid="contact-message"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00BCD4] hover:bg-[#00acc1]"
              data-testid="contact-submit-btn"
            >
              {loading ? "Sending..." : "Send Message"}
            </Button>

            <p className="text-xs text-slate-500 text-center">
              We typically respond within 24 business hours.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSalesForm;
